import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, signal } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { TrackData, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DeviceService, NativeAppService } from '@frontend/vanilla/core';
import { NgxPopperjsContentComponent, NgxPopperjsDirective, NgxPopperjsPlacements, NgxPopperjsTriggers } from 'ngx-popperjs';

import { EpcotConfigService } from '../../../common/epcot-config.service';
import { AdaptiveLayoutService } from '../../../layout/adaptive-layout.service';
import { MyBetsEarlyPayoutTacService } from '../../../my-bets-base/early-payout/tac-dialog/earlypayout-tac.service';
import { CashoutSettingsBetInfo, IAutoCashoutBetslipNumbers } from '../../../my-bets-base/models/early-payout';
import { MyBetsBetSlip, MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { CashoutSettingsService } from '../../services/cash-out-settings.service';
import { MyBetsStrategy } from '../../services/my-bets-display.strategy';
import { MyBetsUtilService } from '../../services/my-bets-util.service';

@Component({
    selector: 'ms-my-bets-earlypayout-settings',
    templateUrl: 'my-bets-earlypayout-settings.html',
    styleUrl: 'my-bets-earlypayout-settings.scss',
})
export class MyBetsEarlyPayoutSettingsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() betslip: MyBetsBetSlip;
    @Input() myBetsListContext: MyBetsListContext;
    @Input() shouldRefreshPosition = false;
    @Input() betTypeInfo: string;
    @Output() autoCashoutPopperTriggered = new EventEmitter<'open' | 'close'>();

    isCashoutSet = false;
    isNotificationSet = false;
    settingsToggled = false;
    private myBetsTrackSuffix = '';
    isEpcotEnabled: boolean;
    autoCashOutValue = signal<number | undefined>(0);
    betCardRedesigned = this.myBetsUtilService.isBetCardRedesigned();

    constructor(
        private adaptiveLayoutSservice: AdaptiveLayoutService,
        public sitecore: Sitecore,
        private cashoutSettingsService: CashoutSettingsService,
        private nativeAppService: NativeAppService,
        private trackingService: TrackingService,
        private myBetsEarlyPayoutTacService: MyBetsEarlyPayoutTacService,
        private timerService: TimerService,
        private deviceService: DeviceService,
        private myBetsStrategy: MyBetsStrategy,
        private epcotConfigService: EpcotConfigService,
        private myBetsUtilService: MyBetsUtilService,
    ) {
        this.myBetsTrackSuffix = this.myBetsStrategy.inColumn ? 'Mini_MyBets' : 'Main_MyBets';
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    get showCircle(): boolean {
        return Boolean((this.isCashoutSet || this.isNotificationSet) && !this.settingsToggled);
    }

    get betslipNumbers(): IAutoCashoutBetslipNumbers {
        return {
            betslipId: this.betslip.betslipId,
            betslipRealId: this.betslip.betslipRealId,
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        const betslipChange = changes['betslip'];

        if (!betslipChange?.isFirstChange()) {
            this.cashoutSettingsService.tryPushUpdateBetInfo(<CashoutSettingsBetInfo>{
                betslipId: betslipChange.currentValue.betslipId,
                earlyPayout: betslipChange.currentValue.earlyPayout,
                possibleWinnings: betslipChange.currentValue.possibleWinnings,
                grossPossibleWinnings: betslipChange.currentValue.grossPossibleWinnings,
                isFreeBet: betslipChange.currentValue.isFreeBet,
                stake: betslipChange.currentValue.stake,
                betType: betslipChange.currentValue.sgpType ? betslipChange.currentValue.sgpType : betslipChange.currentValue.betType,
                containsLiveBets: betslipChange.currentValue.containsLiveBets,
            });
        }
    }

    ngOnInit(): void {
        this.isCashoutSet = !!this.betslip.earlyPayout!.autoCashOutValue;
        this.autoCashOutValue.set(this.betslip.earlyPayout!.autoCashOutValue);
        this.isNotificationSet = this.nativeAppService.isNative && !!this.betslip.earlyPayout!.autoCashoutNotificationValue;

        this.cashoutSettingsService.addBetInfo(<CashoutSettingsBetInfo>{
            betslipId: this.betslip.betslipId,
            earlyPayout: this.betslip.earlyPayout,
            possibleWinnings: this.betslip.possibleWinnings,
            grossPossibleWinnings: this.betslip.grossPossibleWinnings,
            isFreeBet: this.betslip.isFreeBet,
            stake: this.betslip.stake,
            betType: this.betslip.sgpType || this.betslip.betType,
            containsLiveBets: this.betslip.containsLiveBets,
        });
    }

    ngOnDestroy(): void {
        this.cashoutSettingsService.removeBetInfo(this.betslip.betslipId);
    }

    async toggleCashoutSettings($event?: MouseEvent): Promise<void> {
        if (!this.settingsToggled) {
            const labelEvent = 'set fly out open';

            this.trackingService.track(
                trackingConstants.EVENT_AUTO_EARLYPAYOUT_SET,
                this.getAutoCashoutBaseTracking(
                    labelEvent,
                    this.betslip.betslipId,
                    this.betTypeInfo,
                    this.betslip.isFreeBet,
                    this.betslip.containsLiveBets,
                ),
            );
        }

        if (!this.myBetsEarlyPayoutTacService.isEarlyPayoutTac) {
            this.myBetsEarlyPayoutTacService.showTacDialog(this.betslip.containsLiveBets, this.myBetsListContext.isEventView);
            if ($event) {
                $event.stopImmediatePropagation();
                $event.preventDefault();
            }

            return;
        }

        this.autoCashoutPopperTriggered.emit('open');
        this.settingsToggled = true;
    }

    onShown($event: any): void {
        if (this.shouldRefreshPosition) {
            this.timerService.setTimeout(() => ($event.content as NgxPopperjsContentComponent).update());
        }
    }

    closeCashoutSettings(event: NgxPopperjsDirective): void {
        this.autoCashoutPopperTriggered.emit('close');
        this.settingsToggled = false;
        this.cashoutSettingsService.clearErrors(this.betslip.betslipId);
        this.update();

        const labelEvent = 'set fly out closed';

        this.trackingService.track(
            trackingConstants.EVENT_AUTO_EARLYPAYOUT_SET,
            this.getAutoCashoutBaseTracking(
                labelEvent,
                this.betslip.betslipId,
                this.betTypeInfo,
                this.betslip.isFreeBet,
                this.betslip.containsLiveBets,
            ),
        );
    }

    get useLeftOffset(): boolean {
        return (
            (!this.deviceService.isMobile && this.adaptiveLayoutSservice.current.hasRightColumn) ||
            (this.deviceService.isMobile && !this.deviceService.isTablet && this.deviceService.currentOrientation === 'portrait')
        );
    }

    private update(): void {
        const betinfo = this.cashoutSettingsService.getBetInfo(this.betslip.betslipId);
        if (!betinfo) {
            return;
        }

        this.betslip.earlyPayout!.autoCashOutValue = betinfo.earlyPayout!.autoCashOutValue || 0;
        this.betslip.earlyPayout!.autoCashoutNotificationValue = betinfo.earlyPayout!.autoCashoutNotificationValue || 0;
        this.isCashoutSet = !!this.betslip.earlyPayout!.autoCashOutValue;

        this.isNotificationSet = this.nativeAppService.isNative && !!this.betslip.earlyPayout!.autoCashoutNotificationValue;
        this.autoCashOutValue.update(() => this.betslip.earlyPayout!.autoCashOutValue);
    }

    private getAutoCashoutBaseTracking(
        labelEvent: string,
        betslipId: string,
        betType: string,
        isFreeBet: boolean,
        containsLiveBets: boolean,
    ): Partial<TrackData> {
        return {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'early payout auto set up',
            [trackingConstants.COMPONENT_LABEL_EVENT]: labelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'attempt',
            [trackingConstants.COMPONENT_POSITION_EVENT]: betType,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: (containsLiveBets ? 'live/' : 'main/') + this.myBetsTrackSuffix,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: isFreeBet ? 'freebet' : 'real money bet',
            earlyPayoutSlipID: betslipId,
        };
    }

    protected readonly NgxPopperjsTriggers = NgxPopperjsTriggers;
    protected readonly NgxPopperjsPlacements = NgxPopperjsPlacements;
}
